import {
  Box,
  Text,
  Button,
  Hidden,
  IconEdit,
  Badge,
  useToast,
  Link
} from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'

import { parseISO } from 'date-fns'
import { toZonedTime, format } from 'date-fns-tz'
import { LoadingSpinner } from '@/components/loading-circle'
import client from '@/ports/api-client'
import { PropagationBoundary } from '@/components/propagation-boundary'
import OverflowMenuAutoReply from './OverflowMenuAutoReply'

type MessageCardProps = {
  id: string
  title: string
  description: string
  lastUpdate: string
  listingsCount: string
  isActive: string
  trigger: string
  onClick?: () => void | undefined
}

type BodyType = {
  name: string
  content: string
  attach_new_listings: number | string
  is_active: boolean | string
}

const updateAutoReplyMessage = async (body: BodyType, id: string) => {
  const formData = new FormData()
  formData.append('body', JSON.stringify(body))

  const response = await client.put(`/auto_reply_trigger/content/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response
}

const MessageCard = ({
  id,
  title,
  description,
  lastUpdate,
  listingsCount,
  isActive,
  trigger,
  onClick
}: MessageCardProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const showToast = useToast()
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const formattedDate = format(
    toZonedTime(parseISO(lastUpdate.replace(' ', 'T') + 'Z'), localTimezone),
    'MMM do, yyyy h:mm a',
    { timeZone: localTimezone }
  )
  const values = {
    name: title,
    content: description,
    attach_new_listings: listingsCount,
    is_active: isActive === '1' ? '0' : '1'
  }

  const toastMessages = {
    header: (is_active: string) => {
      return is_active === '0'
        ? t('l.settings.view.auto_reply.inactive_header', 'Message set to inactive')
        : t('l.settings.view.auto_reply.active_header', 'Message set to active')
    },
    description: (is_active: string) => {
      return is_active === '0'
        ? t(
            'l.settings.view.auto_reply.inactive_description',
            'This message will no longer be sent automatically.'
          )
        : t(
            'l.settings.view.auto_reply.active_description',
            'The message will be sent to the listings assigned.'
          )
    }
  }

  const { mutate: setInactive_Active, isPending: pendingStatus } = useMutation({
    mutationFn: ({ values, id }: { values: BodyType; id: string }) => {
      return updateAutoReplyMessage(values, id)
    },
    onSuccess: async result => {
      queryClient.invalidateQueries({ queryKey: ['autoreply', 'autoreply_messages'] })
      showToast({
        tone: 'positive',
        message: toastMessages.header(result?.is_active),
        description: toastMessages.description(result?.is_active)
      })
    }
  })

  return (
    <Link href={`/settings/auto-reply/auto-reply-message/edit?trigger=${trigger}&id=${id}`}>
      <Box
        display="flex"
        gap="small"
        justifyContent="spaceBetween"
        padding="medium"
        alignItems="center"
        borderRadius="standard"
        border="borderNeutralLight"
        background="surface"
        key={id}
      >
        <Box display="flex" flexDirection="column" gap="medium">
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap="medium"
            alignItems={{ mobile: 'flexStart', tablet: 'center' }}
          >
            <Text tone={'neutral'} size="medium" weight="strong">
              {title} ({listingsCount})
            </Text>

            {pendingStatus && <LoadingSpinner width={16} height={16} />}
            {isActive === '0' && !pendingStatus && (
              <Badge tone="caution">{t('l.settings.view.auto_reply.inactive', 'Inactive')}</Badge>
            )}
          </Box>

          <Box maxWidth="medium">
            <Box width="full">
              <Text tone={'secondary'} size="medium">
                <em>{`${description.substring(0, 120)} ...`}</em>
              </Text>
            </Box>
          </Box>
          <Text tone="tertiary">
            {' '}
            {t('l.settings.view.auto_reply.last_updated', 'Last updated: ')}
            {formattedDate}
          </Text>
        </Box>
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          <Box>
            <Hidden below="tablet">
              <Box display={'flex'}>
                <Button size="small" variant={'ghost'} id={id} icon={<IconEdit />}>
                  {t('common.edit', 'Edit')}
                </Button>
              </Box>
            </Hidden>
          </Box>
          <PropagationBoundary>
            <OverflowMenuAutoReply
              id={id}
              status={isActive}
              setStatus={() => setInactive_Active({ values, id })}
            />
          </PropagationBoundary>
        </Box>
      </Box>
    </Link>
  )
}

export default MessageCard
